import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { type ReactNode } from 'react';

import { cx } from '@/lib/cx';
import { Button } from '@/ui/primitives/button';
import { Select } from '@/ui/primitives/select';
import { OPCOES_POR_PAGINA } from '@fiscoai/contracts';

import { faixaDaPagina, paginasVisiveis } from './paginas-visiveis';

const OPCOES = OPCOES_POR_PAGINA.map((n) => ({ valor: String(n), rotulo: String(n) }));

export interface PaginacaoProps {
  pagina: number;
  porPagina: number;
  total: number;
  aoMudarPagina: (pagina: number) => void;
  aoMudarPorPagina: (porPagina: number) => void;
  /** Enquanto a página nova carrega: navegação desligada (sem cliques duplos). */
  ocupado?: boolean;
  className?: string;
}

function BotaoPagina(p: {
  rotulo: string;
  desligado: boolean;
  aoClicar: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variante="fantasma"
      tamanho="icone-sm"
      aria-label={p.rotulo}
      disabled={p.desligado}
      onClick={p.aoClicar}
    >
      {p.children}
    </Button>
  );
}

function NumerosDePagina(p: { atual: number; totalPaginas: number; ir: (n: number) => void }) {
  return (
    <ol className="hidden items-center gap-0.5 sm:flex">
      {paginasVisiveis(p.atual, p.totalPaginas).map((item) =>
        typeof item === 'number' ? (
          <li key={item}>
            <button
              type="button"
              aria-current={item === p.atual ? 'page' : undefined}
              aria-label={`Página ${String(item)}`}
              onClick={() => {
                p.ir(item);
              }}
              className={cx(
                'grid h-controle-sm min-w-controle-sm place-items-center rounded-controle px-2 text-sm tabular transition-colors duration-[var(--duracao-rapida)]',
                'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave',
                item === p.atual
                  ? 'bg-marca-suave font-semibold text-marca'
                  : 'text-texto-suave hover:bg-superficie-hover hover:text-texto',
              )}
            >
              {item}
            </button>
          </li>
        ) : (
          <li key={item} aria-hidden className="px-1 text-texto-fraco">
            …
          </li>
        ),
      )}
    </ol>
  );
}

/**
 * Rodapé da tabela: a faixa exibida ("26–50 de 231"), quantos por página e a navegação
 * (primeira, anterior, números com reticências, próxima, última). No celular, "Página 5 de 20".
 */
export function Paginacao(p: PaginacaoProps) {
  const totalPaginas = Math.max(1, Math.ceil(p.total / p.porPagina));
  const ir = (n: number) => {
    p.aoMudarPagina(Math.min(Math.max(1, n), totalPaginas));
  };
  const ocupado = p.ocupado === true;
  return (
    <nav
      aria-label="Paginação"
      className={cx(
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2 text-sm',
        p.className,
      )}
    >
      <div className="flex items-center gap-3">
        <span className="tabular text-texto-suave" aria-live="polite">
          {faixaDaPagina(p.pagina, p.porPagina, p.total)}
        </span>
        <label className="flex items-center gap-2 text-texto-suave">
          <span className="hidden sm:inline">Por página</span>
          <Select
            aria-label="Itens por página"
            className="h-controle-sm w-20 text-sm"
            opcoes={OPCOES}
            valor={String(p.porPagina)}
            aoMudar={(v) => {
              p.aoMudarPorPagina(Number(v));
            }}
          />
        </label>
      </div>
      <div className="flex items-center gap-1">
        <BotaoPagina
          rotulo="Primeira página"
          desligado={ocupado || p.pagina <= 1}
          aoClicar={() => {
            ir(1);
          }}
        >
          <ChevronsLeft aria-hidden />
        </BotaoPagina>
        <BotaoPagina
          rotulo="Página anterior"
          desligado={ocupado || p.pagina <= 1}
          aoClicar={() => {
            ir(p.pagina - 1);
          }}
        >
          <ChevronLeft aria-hidden />
        </BotaoPagina>
        <NumerosDePagina atual={p.pagina} totalPaginas={totalPaginas} ir={ir} />
        <span className="px-2 tabular text-texto-suave sm:hidden">
          Página {p.pagina} de {totalPaginas}
        </span>
        <BotaoPagina
          rotulo="Próxima página"
          desligado={ocupado || p.pagina >= totalPaginas}
          aoClicar={() => {
            ir(p.pagina + 1);
          }}
        >
          <ChevronRight aria-hidden />
        </BotaoPagina>
        <BotaoPagina
          rotulo="Última página"
          desligado={ocupado || p.pagina >= totalPaginas}
          aoClicar={() => {
            ir(totalPaginas);
          }}
        >
          <ChevronsRight aria-hidden />
        </BotaoPagina>
      </div>
    </nav>
  );
}
